'use client';

import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { REGISTER_EMAIL_STORAGE_KEY } from '@/features/auth/constants';
import { registerSchema, type RegisterSchema } from '@/features/auth/schemas';
import { hardRedirect } from '@/features/auth/utils';
import { TermsAndConditions } from '@/features/legal/components';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import { useRegisterMutation } from '../api/authApi';
import { useBootstrapAuth } from '../hooks';

import styles from './AuthForm.module.scss';

const registerFieldSchemas = registerSchema.shape;

type RegisterFormProps = {
  inviteCode?: string;
  invitedEmail?: string;
  termsMarkdownContent?: string;
};

export function RegisterForm({
  inviteCode = '',
  invitedEmail = '',
  termsMarkdownContent = '',
}: RegisterFormProps) {
  const bootstrapAuth = useBootstrapAuth();
  const [register, registerStatus] = useRegisterMutation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const trimmedInviteCode = inviteCode.trim();
  const trimmedInvitedEmail = invitedEmail.trim();

  const [formData, setFormData] = useState<RegisterSchema>({
    first_name: '',
    last_name: '',
    email: trimmedInvitedEmail,
    password: '',
    confirm_password: '',
    accepted_terms: false,
    invite_code: trimmedInviteCode || undefined,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterSchema, string>>
  >({});

  const isBusy = registerStatus.isLoading || isRedirecting;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const parsed = registerSchema.safeParse(formData);

    if (!parsed.success) {
      setErrors(zodErrorsToFieldErrors<RegisterSchema>(parsed.error));
      return;
    }

    try {
      const response = await register({
        ...parsed.data,
        ...(trimmedInviteCode ? { invite_code: trimmedInviteCode } : {}),
      }).unwrap();

      notify({
        type: 'success',
        message:
          (response?.message as string) ??
          'Account created! Please verify your email.',
      });

      const data = response?.data;

      if (data?.user || data?.workspace) {
        setIsRedirecting(true);
        bootstrapAuth({
          currentUser: data.user ?? null,
          currentWorkspaceSlug: data.workspace?.slug ?? null,
          isWorkspaceSetupRequired: data.is_workspace_setup_required ?? false,
          isWorkspaceOnboarded: data.workspace?.is_workspace_onboarded ?? null,
        });
        hardRedirect(appRoutes.home);
        return;
      }

      const rawEmail = data?.email ?? formData.email ?? '';
      const sanitizedEmail = rawEmail.trim();

      if (typeof window !== 'undefined') {
        sessionStorage.setItem(REGISTER_EMAIL_STORAGE_KEY, sanitizedEmail);
      }

      hardRedirect(
        `${appRoutes.auth.verify}?email=${encodeURIComponent(sanitizedEmail)}`,
      );
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form
      className={`${styles.form} ${styles.formActive}`}
      onSubmit={handleSubmit}
    >
      <div className={styles.fieldRow}>
        <SmartInput
          type="text"
          name="first_name"
          label="First name"
          value={formData.first_name}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, first_name: value }))
          }
          schema={registerFieldSchemas.first_name}
          error={errors.first_name}
        />

        <SmartInput
          type="text"
          name="last_name"
          label="Last name"
          value={formData.last_name}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, last_name: value }))
          }
          schema={registerFieldSchemas.last_name}
          error={errors.last_name}
        />
      </div>

      <SmartInput
        type="text"
        name="email"
        label={trimmedInvitedEmail ? 'Email from invite' : 'Email'}
        value={formData.email}
        onChange={(value) =>
          setFormData((prev) => ({
            ...prev,
            email: trimmedInvitedEmail ? prev.email : value,
          }))
        }
        schema={registerFieldSchemas.email}
        error={errors.email}
        readOnly={Boolean(trimmedInvitedEmail)}
      />

      <SmartInput
        type="password"
        name="password"
        label="Password"
        value={formData.password}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, password: value }))
        }
        schema={registerFieldSchemas.password}
        error={errors.password}
      />

      <SmartInput
        type="password"
        name="confirm_password"
        label="Repeat password"
        value={formData.confirm_password}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, confirm_password: value }))
        }
        schema={registerFieldSchemas.confirm_password}
        error={errors.confirm_password}
      />

      <div>
        <div className={styles.checkboxLabel}>
          <SmartInput
            type="checkbox"
            name="accepted_terms"
            label={
              (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  I agree to the{' '}
                  <TermsAndConditions
                    mode="modal"
                    markdownContent={termsMarkdownContent}
                  />
                </div>
              ) as unknown as string
            }
            checked={formData.accepted_terms}
            onChange={(checked) =>
              setFormData((prev) => ({ ...prev, accepted_terms: checked }))
            }
            error={errors.accepted_terms}
          />
        </div>
        {errors.accepted_terms ? (
          <span className={styles.error}>{errors.accepted_terms}</span>
        ) : null}
      </div>

      <MainButton
        type="submit"
        disabled={isBusy}
        isLoading={isBusy}
        ariaLabel="Create account"
      >
        Sign up
      </MainButton>
    </form>
  );
}
